import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchScreener, type ScreenerRow } from './screener-client.js';
import {
  addEntityToWatchlist,
  addEntitiesToWatchlist,
  createScreenerPreset,
  createWatchlist,
  createSyntheticMember,
  deleteSyntheticMember,
  fetchRollup,
  fetchPlatformBuilder,
  type PlatformBuilderQuery,
  fetchAlerts,
  markAlertsRead,
  type SyntheticMemberInput,
  deleteScreenerPreset,
  deleteWatchlist,
  getEntity,
  getEntityActivity,
  getEntityCrossDirectorships,
  getEntityDirectors,
  getEntityFinancials,
  getEntityHealthcarePresence,
  getEntityLists,
  getEntityAggregates,
  getEntityMaEvent,
  getEntitySignals,
  getEntitySignalEvaluations,
  getEntityWatchlists,
  getListEntities,
  getMarkets,
  getMarketOpportunity,
  fetchOrigination,
  fetchOriginationSignal,
  type OriginationSignalQuery,
  getReimbursementMargins,
  getWatchlistEntities,
  listEntities,
  listScreenerPresets,
  listWatchlists,
  removeEntityFromList,
  removeEntityFromWatchlist,
  renameWatchlist,
  transitionEntityToList,
  updateScreenerPreset,
  listBuyerMandates,
  createBuyerMandate,
  updateBuyerMandate,
  deleteBuyerMandate,
  type BuyerMandateInput,
  listBuyerHoldings,
  listBuyerPortfolios,
  createBuyerHolding,
  deleteBuyerHolding,
  type BuyerHoldingInput,
  submitFeedback,
  type FeedbackInput,
  type EntitiesQuery,
  type ListKind,
  type RejectionCategory,
} from './client.js';
import { queryKeys } from './query-client.js';

// Hook factory pattern — components import these, not the raw client.
// Naming mirrors the resource: useMarkets, useEntities, useEntity,
// useEntityFinancials. Each hook owns its query key and its fetcher.

export function useMarkets() {
  return useQuery({
    queryKey: queryKeys.markets(),
    queryFn: getMarkets,
    staleTime: Infinity, // markets are static
  });
}

// Market / Opportunity — cross-market aggregation cells. Recomputed on the
// backend per market refit (≈ daily), so a long staleTime is fine.
export function useMarketOpportunity(
  country?: Parameters<typeof getMarketOpportunity>[0],
  altitude?: Parameters<typeof getMarketOpportunity>[1],
  level?: Parameters<typeof getMarketOpportunity>[2],
) {
  return useQuery({
    queryKey: [
      'market-opportunity',
      altitude ?? 'region',
      country ?? 'all',
      level ?? 'nuts2',
    ] as const,
    queryFn: () => getMarketOpportunity(country, altitude, level),
    staleTime: 5 * 60_000,
  });
}

// Origination Opportunity Score — raw normalised components per region ×
// subsegment; the panel blends them with the user's weights. Only fetched when
// the Origination view is active (gated by `enabled`).
export function useOrigination(
  country?: Parameters<typeof fetchOrigination>[0],
  opts: { enabled?: boolean; level?: Parameters<typeof fetchOrigination>[1] } = {},
) {
  return useQuery({
    queryKey: ['origination', country ?? 'all', opts.level ?? 'nuts2'] as const,
    queryFn: () => fetchOrigination(country, opts.level),
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
  });
}

// Corryk Origination Signal (entity-level) — paged candidate list for a slice.
// Shuffled server-side by a per-session seed; "Load 12 more" pages by offset.
export function useOriginationSignal(params: Omit<OriginationSignalQuery, 'offset'>) {
  return useInfiniteQuery({
    queryKey: ['origination-signal', params] as const,
    queryFn: ({ pageParam }) => fetchOriginationSignal({ ...params, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      // Stop paging (→ hasNextPage false → hide "Load more") once everything
      // above the bar is loaded, or if a page came back empty (guards against a
      // stale aboveBarTotal leaving a dangling button that fetches nothing).
      if (last.entities.length === 0) return undefined;
      const loaded = all.reduce((n, p) => n + p.entities.length, 0);
      return loaded < last.aboveBarTotal ? loaded : undefined;
    },
    staleTime: 60_000,
  });
}

// Reimbursement margin cross-check — observed median operating-margin Δ per
// subsegment from filed P&Ls. Changes only as new accounts file (≈ quarterly),
// so a long staleTime is fine.
export function useReimbursementMargins() {
  return useQuery({
    queryKey: ['reimbursement-margins'] as const,
    queryFn: getReimbursementMargins,
    staleTime: 30 * 60_000,
  });
}

export function useEntities(query: EntitiesQuery) {
  return useQuery({
    queryKey: queryKeys.entitiesList(query),
    queryFn: () => listEntities(query),
  });
}

/**
 * Server-side filtered + paged variant of useEntities for the Screener.
 *
 * `query` carries every filter — country, sub-segment, free-text, the five
 * fundamental ranges, years active, revenue / employee growth y/y. Pass
 * `pageSize` to control the LIMIT per fetch (defaults to 50). The hook
 * returns a TanStack `useInfiniteQuery` result; the Screener flattens
 * `data.pages[].data` into a flat row list for the virtualizer and
 * triggers `fetchNextPage()` from an intersection-observer at the bottom
 * of the list.
 *
 * `query.offset` is ignored — the offset is derived from the page index
 * × pageSize via getNextPageParam. The query key intentionally omits
 * offset so the cached pages stay valid across re-renders that change
 * unrelated fields.
 */
export function useInfiniteEntities(query: Omit<EntitiesQuery, 'offset'>, pageSize = 50) {
  const queryWithLimit: EntitiesQuery = { ...query, limit: pageSize };
  return useInfiniteQuery({
    queryKey: queryKeys.entitiesList(queryWithLimit),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => listEntities({ ...queryWithLimit, offset: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.data.length, 0);
      if (fetched >= lastPage.pagination.total) return undefined;
      return fetched;
    },
  });
}

export function useEntity(id: string | null) {
  return useQuery({
    queryKey: queryKeys.entity(id ?? ''),
    queryFn: () => {
      if (!id) throw new Error('useEntity called without an id');
      return getEntity(id);
    },
    enabled: !!id,
  });
}

export function useEntityFinancials(id: string | null) {
  return useQuery({
    queryKey: queryKeys.entityFinancials(id ?? ''),
    queryFn: () => {
      if (!id) throw new Error('useEntityFinancials called without an id');
      return getEntityFinancials(id);
    },
    enabled: !!id,
  });
}

export function useEntitySignals(id: string | null) {
  return useQuery({
    queryKey: ['entities', id ?? '', 'signals'] as const,
    queryFn: () => {
      if (!id) throw new Error('useEntitySignals called without an id');
      return getEntitySignals(id);
    },
    enabled: !!id,
  });
}

export function useEntityAggregates(ids: string[]) {
  const key = ids.join(',');
  return useQuery({
    queryKey: ['entities', 'aggregates', key] as const,
    queryFn: () => getEntityAggregates(ids),
    enabled: ids.length > 0,
    staleTime: 60_000,
  });
}

export function useEntitySignalEvaluations(id: string | null) {
  return useQuery({
    queryKey: ['entities', id ?? '', 'signal-evaluations'] as const,
    queryFn: () => {
      if (!id) throw new Error('useEntitySignalEvaluations called without an id');
      return getEntitySignalEvaluations(id);
    },
    enabled: !!id,
  });
}

export function useEntityDirectors(id: string | null) {
  return useQuery({
    queryKey: ['entities', id ?? '', 'directors'] as const,
    queryFn: () => {
      if (!id) throw new Error('useEntityDirectors called without an id');
      return getEntityDirectors(id);
    },
    enabled: !!id,
  });
}

export function useEntityActivity(id: string | null) {
  return useQuery({
    queryKey: ['entities', id ?? '', 'activity'] as const,
    queryFn: () => {
      if (!id) throw new Error('useEntityActivity called without an id');
      return getEntityActivity(id);
    },
    enabled: !!id,
  });
}

export function useEntityHealthcarePresence(id: string | null) {
  return useQuery({
    queryKey: ['entities', id ?? '', 'healthcare-presence'] as const,
    queryFn: () => {
      if (!id) throw new Error('useEntityHealthcarePresence called without an id');
      return getEntityHealthcarePresence(id);
    },
    enabled: !!id,
  });
}

export function useEntityCrossDirectorships(id: string | null) {
  return useQuery({
    queryKey: ['entities', id ?? '', 'cross-directorships'] as const,
    queryFn: () => {
      if (!id) throw new Error('useEntityCrossDirectorships called without an id');
      return getEntityCrossDirectorships(id);
    },
    enabled: !!id,
  });
}

export function useEntityMaEvent(id: string | null) {
  return useQuery({
    queryKey: ['entities', id ?? '', 'ma-event'] as const,
    queryFn: () => {
      if (!id) throw new Error('useEntityMaEvent called without an id');
      return getEntityMaEvent(id);
    },
    enabled: !!id,
  });
}

// --- screener presets ----------------------------------------------

export function useScreenerPresets() {
  return useQuery({
    queryKey: ['screener-presets'] as const,
    queryFn: () => listScreenerPresets(),
    staleTime: 60_000,
  });
}

export function useCreateScreenerPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; filterJson: Record<string, unknown> }) =>
      createScreenerPreset(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['screener-presets'] });
    },
  });
}

export function useUpdateScreenerPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; name?: string; filterJson?: Record<string, unknown> }) =>
      updateScreenerPreset(args.id, { name: args.name, filterJson: args.filterJson }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['screener-presets'] });
    },
  });
}

export function useDeleteScreenerPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteScreenerPreset(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['screener-presets'] });
    },
  });
}

// ── Buyer mandates (Phase 2) ─────────────────────────────────────────────────
export function useBuyerMandates() {
  return useQuery({
    queryKey: ['buyer-mandates'] as const,
    queryFn: () => listBuyerMandates(),
    staleTime: 60_000,
  });
}

export function useCreateBuyerMandate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BuyerMandateInput) => createBuyerMandate(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['buyer-mandates'] });
    },
  });
}

export function useUpdateBuyerMandate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string } & Partial<BuyerMandateInput>) => {
      const { id, ...patch } = args;
      return updateBuyerMandate(id, patch);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['buyer-mandates'] });
    },
  });
}

export function useDeleteBuyerMandate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteBuyerMandate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['buyer-mandates'] });
    },
  });
}

// ── Buyer portfolios / holdings (Phase 2 + conditions layer) ─────────────────
export function useBuyerPortfolios() {
  return useQuery({
    queryKey: ['buyer-portfolios'] as const,
    queryFn: () => listBuyerPortfolios(),
    staleTime: 60_000,
  });
}

export function useBuyerHoldings(portfolioId?: string) {
  return useQuery({
    queryKey: ['buyer-holdings', portfolioId ?? 'all'] as const,
    queryFn: () => listBuyerHoldings(portfolioId),
    staleTime: 60_000,
  });
}

export function useCreateBuyerHolding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BuyerHoldingInput) => createBuyerHolding(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['buyer-holdings'] });
      void qc.invalidateQueries({ queryKey: ['buyer-portfolios'] });
    },
  });
}

export function useDeleteBuyerHolding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteBuyerHolding(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['buyer-holdings'] });
      void qc.invalidateQueries({ queryKey: ['buyer-portfolios'] });
    },
  });
}

export function useSubmitFeedback() {
  return useMutation({
    mutationFn: (input: FeedbackInput) => submitFeedback(input),
  });
}

export function useList(kind: ListKind) {
  return useQuery({
    queryKey: ['lists', kind] as const,
    queryFn: () => getListEntities(kind),
    staleTime: 30_000,
  });
}

export function useEntityLists(id: string | null) {
  return useQuery({
    queryKey: ['entities', id ?? '', 'lists'] as const,
    queryFn: () => {
      if (!id) throw new Error('useEntityLists called without an id');
      return getEntityLists(id);
    },
    enabled: !!id,
  });
}

export function useTransitionEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      entityId: string;
      kind: ListKind;
      rejectionCategory?: RejectionCategory;
      rationaleText?: string;
    }) =>
      transitionEntityToList(args.entityId, args.kind, {
        rejectionCategory: args.rejectionCategory,
        rationaleText: args.rationaleText,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['lists'] });
      void qc.invalidateQueries({ queryKey: ['entities', vars.entityId, 'lists'] });
    },
  });
}

export function useRemoveFromList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { entityId: string; kind: ListKind }) =>
      removeEntityFromList(args.entityId, args.kind),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['lists'] });
      void qc.invalidateQueries({ queryKey: ['entities', vars.entityId, 'lists'] });
    },
  });
}

// --- named watchlists ----------------------------------------------

// Normal watchlists only (the Ward list). Roll-Up Foundations (kind 'rollup')
// are kept OUT of here — they have their own list via useRollupFoundations —
// so the two never mix in the Ward picker or the add-to-watchlist menu.
export function useWatchlists() {
  return useQuery({
    queryKey: ['watchlists', 'watchlist'] as const,
    queryFn: () => listWatchlists('watchlist'),
    staleTime: 30_000,
  });
}

export function useWatchlistEntities(id: string | null) {
  return useQuery({
    queryKey: ['watchlists', id ?? '', 'entities'] as const,
    queryFn: () => {
      if (!id) throw new Error('useWatchlistEntities called without an id');
      return getWatchlistEntities(id);
    },
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** Watchlist members rendered in the exact same shape as the Screener.
 *  Two-step: fetch the member entity ids, then pull those rows from the
 *  screener endpoint (entityIds filter) so the Watchlist table can reuse
 *  SCREENER_COLUMN_DEFS / renderCell verbatim — financials, P-values,
 *  quality tier, the lot — instead of a separate, divergent data shape. */
export function useWatchlistAsScreener(id: string | null): {
  data: ScreenerRow[];
  total: number;
  isLoading: boolean;
  error: unknown;
  isError: boolean;
} {
  const ids = useQuery({
    queryKey: ['watchlists', id ?? '', 'member-ids'] as const,
    queryFn: async () => {
      if (!id) throw new Error('useWatchlistAsScreener called without an id');
      const { data } = await getWatchlistEntities(id);
      return data.map((r) => r.id);
    },
    enabled: !!id,
    staleTime: 30_000,
  });
  const rows = useQuery({
    queryKey: ['watchlists', id ?? '', 'screener-rows', ids.data ?? []] as const,
    queryFn: async () => {
      const entityIds = ids.data ?? [];
      if (entityIds.length === 0) return { rows: [], total: 0 };
      // Cap at the screener's max — watchlists shouldn't ever exceed this
      // in practice, but the limit keeps the query bounded.
      return fetchScreener({ entityIds, limit: 10000 });
    },
    enabled: !!id && ids.isSuccess,
    staleTime: 30_000,
  });
  return {
    data: rows.data?.rows ?? [],
    total: rows.data?.total ?? 0,
    isLoading: ids.isLoading || rows.isLoading,
    error: ids.error ?? rows.error,
    isError: ids.isError || rows.isError,
  };
}

export function useEntityWatchlists(id: string | null) {
  return useQuery({
    queryKey: ['entities', id ?? '', 'watchlists'] as const,
    queryFn: () => {
      if (!id) throw new Error('useEntityWatchlists called without an id');
      return getEntityWatchlists(id);
    },
    enabled: !!id,
  });
}

export function useCreateWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createWatchlist(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['watchlists'] });
    },
  });
}

// --- Roll-Up Simulator ---------------------------------------------

/** The user's Roll-Up Foundations (watchlists of kind 'rollup'). */
export function useRollupFoundations() {
  return useQuery({
    queryKey: ['watchlists', 'rollup'] as const,
    queryFn: () => listWatchlists('rollup'),
    staleTime: 30_000,
  });
}

export function useCreateRollupFoundation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createWatchlist(name, 'rollup'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['watchlists'] });
    },
  });
}

/** Pro-forma combine (real members + synthetic) for a foundation. */
export function useRollup(watchlistId: string | null) {
  return useQuery({
    queryKey: ['rollup', watchlistId ?? ''] as const,
    queryFn: () => {
      if (!watchlistId) throw new Error('useRollup called without a watchlistId');
      return fetchRollup(watchlistId);
    },
    enabled: !!watchlistId,
    staleTime: 30_000,
  });
}

/** Platform Builder — construct a buy-list for a target + market. Null query =
 *  inputs incomplete (disabled). */
export function usePlatformBuilder(query: PlatformBuilderQuery | null) {
  return useQuery({
    queryKey: ['platform-builder', query] as const,
    queryFn: () => {
      if (!query) throw new Error('usePlatformBuilder called without a query');
      return fetchPlatformBuilder(query);
    },
    enabled: !!query,
    staleTime: 30_000,
  });
}

export function useAddSyntheticMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { watchlistId: string; input: SyntheticMemberInput }) =>
      createSyntheticMember(v.watchlistId, v.input),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['rollup', v.watchlistId] });
      void qc.invalidateQueries({ queryKey: ['watchlists'] });
    },
  });
}

export function useDeleteSyntheticMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { watchlistId: string; memberId: string }) =>
      deleteSyntheticMember(v.watchlistId, v.memberId),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['rollup', v.watchlistId] });
    },
  });
}

export function useRenameWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; name: string }) => renameWatchlist(args.id, args.name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['watchlists'] });
    },
  });
}

export function useDeleteWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWatchlist(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['watchlists'] });
    },
  });
}

export function useAddEntityToWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { watchlistId: string; entityId: string }) =>
      addEntityToWatchlist(args.watchlistId, args.entityId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['watchlists'] });
      void qc.invalidateQueries({
        queryKey: ['watchlists', vars.watchlistId, 'entities'],
      });
      void qc.invalidateQueries({ queryKey: ['entities', vars.entityId, 'watchlists'] });
      // The watchlist may be a Roll-Up Foundation — refresh its combine so a
      // newly-added member shows in the Roll-Up panel without a page reload.
      void qc.invalidateQueries({ queryKey: ['rollup', vars.watchlistId] });
    },
  });
}

export function useAddEntitiesToWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { watchlistId: string; entityIds: string[] }) =>
      addEntitiesToWatchlist(args.watchlistId, args.entityIds),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['watchlists'] });
      void qc.invalidateQueries({ queryKey: ['watchlists', vars.watchlistId, 'entities'] });
      void qc.invalidateQueries({ queryKey: ['rollup', vars.watchlistId] });
    },
  });
}

export function useRemoveEntityFromWatchlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { watchlistId: string; entityId: string }) =>
      removeEntityFromWatchlist(args.watchlistId, args.entityId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['watchlists'] });
      void qc.invalidateQueries({
        queryKey: ['watchlists', vars.watchlistId, 'entities'],
      });
      void qc.invalidateQueries({ queryKey: ['entities', vars.entityId, 'watchlists'] });
      // The watchlist may be a Roll-Up Foundation — refresh its combine so a
      // newly-added member shows in the Roll-Up panel without a page reload.
      void qc.invalidateQueries({ queryKey: ['rollup', vars.watchlistId] });
    },
  });
}

// --- Alerts / monitoring feed ----------------------------------------

export function useAlerts() {
  return useQuery({
    queryKey: ['alerts'] as const,
    queryFn: fetchAlerts,
    staleTime: 60_000,
    // Poll a few times an hour so a freshly-computed alert surfaces without a
    // manual reload; the daily compute job is the real cadence.
    refetchInterval: 5 * 60_000,
  });
}

export function useMarkAlertsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { ids?: string[]; all?: boolean }) => markAlertsRead(v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['alerts'] }),
  });
}
